import  { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReservationMutation, useGetReservQuery, useUpdateReservMutation } from '../../redux/services/rooms/reservationAPI';
import { addDays } from 'date-fns';
import queryString from 'query-string';
import { useFormik } from 'formik';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Swal from 'sweetalert2';
import { useGetRoomQuery, useAddRateMutation } from '../../redux/services/rooms/roomAPI';
import { useGetCartQuery } from '../../redux/services/userPnael/userPnaelAPI';

const formatApiDate = (dateTime) => {
  const date = new Date(dateTime);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return [date.getFullYear(), month, day].join('-');
};

const excludeRanges = (startDateRange, endDateRange) => {
  const excludeDates = [];
  const cursor = new Date(startDateRange);
  const end = new Date(endDateRange);

  while (cursor <= end) {
    excludeDates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return excludeDates;
};

const getRatingAverage = (ratings, roomId) => {
  const matches = (ratings || []).filter((rate) => Number(rate.room) === Number(roomId));
  if (!matches.length) return 0;
  return matches.reduce((sum, rate) => sum + Number(rate.score || 0), 0) / matches.length;
};

function RoomDetail({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const room = location?.state?.data;
  const existingReservation = location?.state?.reservation;
  const [reservation, { error }] = useReservationMutation();
  const { data: reservData, refetch: refetchReserv } = useGetReservQuery();
  const [addRate, { error: rateError, isError: rateIsError }] = useAddRateMutation();
  const { data: rooms } = useGetRoomQuery();
  const userId = user?.id || user?.user?.id;
  const { refetch: Cartrefetch } = useGetCartQuery(userId);
  const [endDate, setEndDate] = useState(existingReservation ? addDays(new Date(existingReservation.exit_date), 1) : addDays(new Date(), 2));
  const [startDate, setStartDate] = useState(existingReservation ? addDays(new Date(existingReservation.entry_date), 1) : new Date());
  const [disabledDates, setDisabledDates] = useState([]);
  const [updateReserv] = useUpdateReservMutation();

  const ratings = rooms?.ser_room_rating || [];
  const ratingAverage = getRatingAverage(ratings, room?.id);
  const nights = Math.max(1, Math.ceil(Math.abs(new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)));
  const stayTotal = room ? nights * Number(room.price || 0) : 0;

  const form = useFormik({
    initialValues: {
      persons_number: existingReservation?.persons_number || 2,
    },
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!user) {
        Swal.fire({
          icon: 'info',
          html: 'Please <a href="/login">login</a> or <a href="/register">register</a> to book a room.',
          confirmButtonText: 'Got it',
          customClass: { popup: 'sweetalert2-custom-popup' },
        });
        setSubmitting(false);
        return;
      }

      const entryDate = formatApiDate(startDate);
      const exitDate = formatApiDate(endDate);
      const payload = {
        entry_date: [entryDate],
        exit_date: [exitDate],
        reservatore: userId,
        room: room.id,
        persons_number: Number(values.persons_number),
      };

      try {
        await Cartrefetch();
        const response = existingReservation
          ? await updateReserv({ ...payload, id: existingReservation.id, entry_date: entryDate, exit_date: exitDate, reservatore: userId, room: room.id, persons_number: Number(values.persons_number) })
          : await reservation(queryString.stringify(payload));

        if (response?.data) {
          Swal.fire({
            icon: 'success',
            text: response.data.message,
            timer: 1800,
            showConfirmButton: false,
            customClass: { popup: 'sweetalert2-custom-popup' },
          });
          resetForm();
          await Cartrefetch();
          navigate('/cart');
        } else if (response?.error?.data) {
          Swal.fire({
            icon: 'warning',
            text: response.error.data?.non_field_errors?.[0] || response.error.data?.message || 'Unable to complete booking.',
            confirmButtonText: 'OK',
            customClass: { popup: 'sweetalert2-custom-popup' },
          });
        } else {
          Swal.fire({
            icon: 'warning',
            text: 'Unknown error occurred.',
            confirmButtonText: 'OK',
            customClass: { popup: 'sweetalert2-custom-popup' },
          });
        }
      } catch (erro) {
        Swal.fire({
          icon: 'info',
          html: 'Please <a href="/login">login</a> or <a href="/register">register</a> to book a room.',
          confirmButtonText: 'Got it',
          customClass: { popup: 'sweetalert2-custom-popup' },
        });
      } finally {
        setTimeout(() => setSubmitting(false), 300);
      }
    },
    validate: (values) => {
      const errors = {};
      if (!values.persons_number) errors.persons_number = 'Guests are required';
      if (Number(values.persons_number) < 1) errors.persons_number = 'At least one guest is required';
      return errors;
    },
  });

  useEffect(() => {
    refetchReserv();
    if (!room) return;

    const roomReservations = (reservData || []).filter((reserv) => Number(reserv.room) === Number(room.id));
    const blocked = roomReservations.flatMap((reserv) => excludeRanges(addDays(new Date(reserv.entry_date), 1), addDays(new Date(reserv.exit_date), 1)));

    if (existingReservation) {
      const ownBlocked = excludeRanges(addDays(new Date(existingReservation.entry_date), 1), addDays(new Date(existingReservation.exit_date), 1));
      setDisabledDates(blocked.filter((date) => !ownBlocked.some((ownDate) => ownDate.toDateString() === date.toDateString())));
    } else {
      setDisabledDates(blocked);
    }
  }, [reservData, room, existingReservation, refetchReserv]);

  const handleRating = async (newValue) => {
    if (!user) {
      Swal.fire({
        icon: 'info',
        html: 'Please <a href="/login">login</a> to rate this room.',
        confirmButtonText: 'Got it',
        customClass: { popup: 'sweetalert2-custom-popup' },
      });
      return;
    }

    if (rateIsError) {
      Swal.fire({
        icon: 'error',
        text: rateError?.data || 'Unable to submit rating.',
        confirmButtonText: 'Try again',
        customClass: { popup: 'sweetalert2-custom-popup' },
      });
      return;
    }

    const response = await addRate(queryString.stringify({
      score: newValue,
      room: room.id,
      user_liked: userId,
    }));

    if (response?.data) {
      Swal.fire({
        icon: 'success',
        text: response.data.message,
        timer: 1500,
        showConfirmButton: false,
        customClass: { popup: 'sweetalert2-custom-popup' },
      });
      refetchReserv();
    } else {
      Swal.fire({
        icon: 'warning',
        text: response?.error?.data?.message || 'Unable to submit rating.',
        confirmButtonText: 'OK',
        customClass: { popup: 'sweetalert2-custom-popup' },
      });
    }
  };

  if (!room) {
    return (
      <div className="section-shell py-24 text-center">
        <h1 className="section-title">Room not found</h1>
        <p className="section-copy mx-auto">We could not find the room details for this booking.</p>
      </div>
    );
  }

  return (
    <section className="bg-surface py-12 sm:py-16">
      <div className="section-shell">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="premium-card bg-white p-4 sm:p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <img className="h-[360px] w-full rounded-3xl object-cover md:col-span-2" src={room.main_img} alt={room.room_name} />
              <div className="grid grid-cols-2 gap-4 md:col-span-2">
                {(room.room_gallery || []).slice(0, 4).map((roomImage) => (
                  <img key={roomImage.id} className="h-40 w-full rounded-2xl object-cover" src={roomImage.room_img} alt={`${room.room_name} gallery`} loading="lazy" />
                ))}
              </div>
            </div>
          </div>

          <div className="premium-card bg-white p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-gotrip-700">{room.room_styles?.room_type || 'Premium stay'}</p>
                <h1 className="mt-2 text-3xl font-black tracking-tight text-ink">{room.room_name}</h1>
              </div>
              <div className="rounded-2xl bg-amber-50 px-4 py-3 text-right">
                <p className="text-xs font-black uppercase tracking-wide text-amber-600">Rating</p>
                <p className="text-xl font-black text-amber-600">{ratingAverage.toFixed(1)}</p>
              </div>
            </div>

            <p className="mt-5 leading-7 text-muted">{room.description}</p>

            <div className="mt-6 flex flex-wrap gap-3">
              <span className="rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-muted">{room.beds} beds</span>
              <span className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700">Available now</span>
              <span className="rounded-full bg-gotrip-50 px-4 py-2 text-sm font-bold text-gotrip-700">Instant confirmation</span>
            </div>

            <div className="mt-6 rounded-3xl bg-slate-50 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold uppercase tracking-wide text-muted">Nightly price</p>
                  <p className="mt-1 text-3xl font-black text-ink">${room.price}</p>
                </div>
                <div className="grid place-items-center rounded-full bg-white p-3 shadow-sm text-gotrip-700">
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                </div>
              </div>
              <div className="mt-5 border-t border-line pt-4">
                <div className="flex justify-between text-sm font-bold text-muted">
                  <span>{nights} night{nights > 1 ? 's' : ''}</span>
                  <span>${stayTotal}</span>
                </div>
                <div className="mt-2 flex justify-between text-lg font-black text-ink">
                  <span>Total</span>
                  <span>${stayTotal}</span>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Entry date</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => date && setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  minDate={new Date()}
                  excludeDates={disabledDates}
                  className="field"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Exit date</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => date && setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={new Date()}
                  excludeDates={disabledDates}
                  className="field"
                />
              </div>
              <div>
                <label htmlFor="persons_number" className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Guests</label>
                <input id="persons_number" name="persons_number" type="number" min="1" className="field" value={form.values.persons_number} onChange={form.handleChange} onBlur={form.handleBlur} />
                {form.errors.persons_number && <p className="mt-2 text-sm font-bold text-red-500">{form.errors.persons_number}</p>}
              </div>
            </div>

            <div className="mt-6">
              {user ? (
                <Stack spacing={1}>
                  <Rating
                    value={ratings.find((rate) => Number(rate.user_liked) === Number(userId) && Number(rate.room) === Number(room.id))?.score || 0}
                    onChange={(_, newValue) => handleRating(newValue)}
                    precision={0.5}
                  />
                </Stack>
              ) : (
                <p className="text-sm text-muted">Log in to rate this room.</p>
              )}
            </div>

            <button type="button" disabled={form.isSubmitting} onClick={form.handleSubmit} className="btn-primary mt-6 w-full">
              {existingReservation ? 'Update booking' : 'Book room'}
            </button>
            {error && <p className="mt-3 text-sm font-bold text-red-500">{error?.data?.message || 'Unable to complete booking.'}</p>}
          </div>
        </div>
      </div>
    </section>
  );
}

export default RoomDetail;
